<template>
  <div>
    <q-select
      v-model="model"
      use-input
      input-debounce="0"
      :label="label"
      :option-label="item => `${item.firstName} ${item.lastName}`"
      option-value="id"
      :emit-value="true"
      map-options
      :loading="membersLoading || dataLoading"
      :options="options"
      @filter="filterFn"
      v-bind="siteCFG.fields.general"
      :clearable="clearable"
      @input="$emit('changed', model)"
      :dark="dark"
      :color="color"
    >
      <template
        v-slot:option="{ itemProps, opt, selected, toggleOption, itemEvents }"
      >
        <q-item @click="toggleOption(opt)" v-bind="itemProps" v-on="itemEvents">
          <q-item-section>
            <q-item-label v-if="opt.messages && opt.messages.length > 0">
              {{ opt.firstName }} {{ opt.lastName }}
              <q-icon name="mdi-alert"></q-icon>
            </q-item-label>
            <q-item-label v-else>
              {{ opt.firstName }} {{ opt.lastName }}
            </q-item-label>
            <q-item-label caption>{{ opt.crm_id }}</q-item-label>
            <q-tooltip v-if="opt.messages && opt.messages.length > 0">
              <h6 class="q-my-xs" style="font-size: 14px">
                Unfortunately {{ opt.firstName }} {{ opt.lastName }} cannot be
                entered for this event because:
              </h6>
              <div
                v-for="(message, index) in opt.messages"
                :key="`message_${index}`"
                style="font-size: 12px"
              >
                {{ message }}
              </div>
            </q-tooltip>
          </q-item-section>
        </q-item>
      </template>
      <template v-slot:no-option>
        <q-item>
          <q-item-section class="text-grey"> No results </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  props: {
    id: {
      type: Number,
      default: null
    },
    label: {
      type: String,
      default: "PAM Member Lookup"
    },
    readonly: {
      type: Boolean,
      default: false
    },
    filterToEvent: {
      default: null
    },
    filterToPlayerLabel: {
      default: null
    },
    excludeIDList: {
      default: null
    },
    includeOnlyIDList: {
      default: null
    },
    clearable: {
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "primary"
    },
    specificMembers: null,
    dataLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
      members: "associations/members",
      membersLoading: "associations/membersLoading",
      siteCFG: "settings/siteCFG",
      competition: "competitions/current",
      divisions: "divisions/list"
    })
  },
  watch: {
    id: function() {
      this.init();
    },
    members: function() {
      this.init();
    },
    specificMembers: function() {
      this.init();
    }
  },
  data: () => ({
    model: null,
    options: []
  }),
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.options = JSON.parse(
        JSON.stringify(
          this.specificMembers ? this.specificMembers : this.members
        )
      );
      this.model = this.id;
      this.$forceUpdate();
    },
    filterFn(val, update) {
      if (val === "") {
        update(() => {
          this.options = JSON.parse(
            JSON.stringify(
              this.specificMembers ? this.specificMembers : this.members
            )
          ).filter(v => {
            let eventFilter = true;
            if (this.filterToEvent) {
              eventFilter = this.runFilterToEvent(v);
            }

            return eventFilter;
          });
          this.options.sort((a, b) => {
            if (
              a.messages &&
              a.messages.length > 0 &&
              (!b.messages || b.messages.length == 0)
            ) {
              return 1;
            } else if (
              (!a.messages || a.messages.length == 0) &&
              b.messages &&
              b.messages.length > 0
            ) {
              return -1;
            } else {
              return 0;
            }
          });
        });
        return;
      } else {
        update(() => {
          const needle = val.toLowerCase();
          let members = this.specificMembers
            ? this.specificMembers
            : this.members;
          this.options = members.filter(v => {
            let textMatch =
              `${v.firstName} ${v.lastName} ${v.crm_id}`
                .toLowerCase()
                .indexOf(needle) > -1;
            let eventFilter = true;
            if (this.filterToEvent) {
              eventFilter = this.runFilterToEvent(v);
            }

            return textMatch && eventFilter ? true : false;
          });
          this.options.sort((a, b) => {
            if (
              a.messages &&
              a.messages.length > 0 &&
              (!b.messages || b.messages.length == 0)
            ) {
              return 1;
            } else if (
              (!a.messages || a.messages.length == 0) &&
              b.messages &&
              b.messages.length > 0
            ) {
              return -1;
            } else {
              return 0;
            }
          });
        });
      }
    },
    runFilterToEvent(v) {
      // console.log(v.division_id);
      let passed = false;
      let age = moment(this.competition.age_reference_date).diff(
        v.birthDate,
        "years",
        false
      );

      // TODO: Need to allow for the award that allows for any age
      if (
        (this.filterToEvent.gender == "X" ||
          this.filterToEvent.gender == v.gender ||
          this.filterToPlayerLabel.mixed == 1) &&
        ((age >= this.filterToEvent.age_min &&
          age <= this.filterToEvent.age_max) ||
          (this.filterToPlayerLabel.noMaxAge == 1 && age >= 13)) &&
        (!this.excludeIDList || !this.excludeIDList.includes(v.id)) &&
        (!this.includeOnlyIDList || this.includeOnlyIDList.includes(v.id))
      ) {
        passed = true;
      } else {
        return false;
      }

      if (this.filterToPlayerLabel && v.crm_id) {
        let result = this.checkAwards(
          this.filterToPlayerLabel,
          this.filterToEvent,
          v
        );
        v.messages = result.messages;
        passed = result.display;
        v.disable = !result.passed;
      }

      return passed;
    },

    checkAwards(playerLabels, event, member) {
      let result = {
        passed: true,
        display: true,
        messages: []
      };

      let age = moment(this.competition.age_reference_date).diff(
        member.birthDate,
        "years",
        false
      );

      // Check crew award
      if (playerLabels.crew == 1) {
        let award = member.awards.find(a => a.awards_id == 4);
        if (!award) {
          result.passed = false;
          result.messages.push("Athlete has no valid IRB Crew award");
        }
      }

      // Check driver award
      if (playerLabels.driver == 1) {
        let award = member.awards.find(
          a =>
            (a.awards_id == 2 || a.awards_id == 6) &&
            moment(a.refresher_date).add(1, "days") >=
              moment(event.refresher_reference_date)
        );
        if (!award) {
          result.passed = false;
          result.messages.push("Athlete has no valid IRB Driver award");
        }
      }

      // Check patrolSupport award
      if (playerLabels.patrolSupport == 1 && age >= 13) {
        let award = member.awards.find(
          a =>
            (a.awards_id == 15 ||
              a.awards_id == 11 ||
              a.awards_id == 2 ||
              a.awards_id == 5 ||
              a.awards_id == 6 ||
              a.awards_id == 22 ||
              a.awards_id == 23) &&
            moment(a.refresher_date).add(1, "days") >=
              moment(event.refresher_reference_date)
        );
        if (!award) {
          result.passed = false;
          result.messages.push(
            "Athlete has no valid Patrol Support or SLG award"
          );
        }
      }

      // check slg award
      if (playerLabels.slg == 1 && age >= 13) {
        let award = member.awards.find(
          a =>
            (a.awards_id == 22 || a.awards_id == 23) &&
            moment(a.refresher_date).add(1, "days") >=
              moment(event.refresher_reference_date)
        );
        if (!award) {
          result.passed = false;
          result.messages.push("Athlete has no valid SLG award");
        }
      }

      // check twoHundredMeter award
      if (playerLabels.twoHundredMeter == 1 && age < 14) {
        // Calculating the members age because this award only applies after they are 14
        // let age = moment(event.refresher_reference_date).diff(
        //   member.birthDate,
        //   "years",
        //   false
        // );

        let award = member.awards.find(
          a =>
            (a.awards_id == 3 || a.awards_id == 17) &&
            moment(a.refresher_date).add(1, "days") >=
              moment(event.refresher_reference_date)
        );
        if (!award) {
          result.passed = false;
          result.messages.push("Athlete has no valid 200m award");
        }
      }

      return result;
    }
  }
};
</script>
